/* eslint-disable react/no-multi-comp */


import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { List } from 'react-window';

import { Icon, IconClose, IconGlobeBlack, IconSearch, IconShare, IconUserGroup } from '../../../base/icons';
import { getParticipantDisplayName } from '../../../base/participants';
import { shareNotes } from '../../actions';
import { SHARE_AUDIENCE_EVERYONE, SHARE_AUDIENCE_SELECTED, SHARE_PERMISSION_EDIT, SHARE_PERMISSION_VIEW } from '../../constants';

import NotesSharePerson, { ROW_HEIGHT, toPermission } from './NotesSharePerson';

const LockIcon = () => (
    <svg
        aria-hidden = 'true'
        fill = 'none'
        height = '13'
        stroke = 'currentColor'
        strokeLinecap = 'round'
        strokeLinejoin = 'round'
        strokeWidth = '2'
        viewBox = '0 0 24 24'
        width = '13'>
        <rect
            height = '10'
            rx = '2'
            width = '16'
            x = '4'
            y = '11' />
        <path d = 'M8 11V7a4 4 0 0 1 8 0v4' />
    </svg>
);

/**
 * Share status chip and toggle for our notes. The toggle opens a dialog that
 * covers the whole notes pane, like the participants pane, so long participant
 * lists get room, a search box and a virtualized list. The dialog holds a local
 * draft — audience, per-person selection and per-person or room-wide view/edit
 * permission — until Share; Cancel leaves the live settings untouched. Stop
 * sharing is a separate explicit action that resets everything, even an
 * 'everyone' share with nobody currently in the room.
 *
 * @param {Object} props - Component props.
 * @param {Function} props.t - Translation function.
 * @returns {React$Element}
 */
export default function NotesShare({ t }) {
    const dispatch = useDispatch();
    const store = useStore();
    const dialogRef = useRef(null);
    const toggleRef = useRef(null);

    // The raw list keeps a stable reference, so the pane does not re-render on
    // every unrelated store update.
    const participants = useSelector(state => state['features/base/participants']);
    const {
        everyonePermission,
        saveStatus,
        shareAudience,
        sharePermissions,
        sharedWith
    } = useSelector(state => state['features/notes']);
    const [ draft, setDraft ] = useState(null);
    const [ query, setQuery ] = useState('');
    const remote = useMemo(() => (participants || [])
        .filter(p => !p.local && !p.fakeParticipant)
        .map(p => {
            return { id: p.id,
                name: getParticipantDisplayName(store.getState(), p.id) };
        }), [ participants ]);
    const needle = query.trim().toLowerCase();
    const rows = useMemo(() => {
        if (!needle) {
            return remote;
        }

        return remote.filter(p => p.name.toLowerCase().includes(needle));
    }, [ remote, needle ]);
    const sharing = sharedWith.length > 0 || shareAudience === SHARE_AUDIENCE_EVERYONE;
    const isOpen = Boolean(draft);

    useEffect(() => {
        isOpen && dialogRef.current?.focus();
    }, [ isOpen ]);

    // Never open the picker while saved notes failed to load: applying an empty
    // draft would broadcast the empty buffer over the stored notes.
    const pickerDisabled = saveStatus === 'loadError';
    const open = () => {
        setQuery('');
        setDraft({
            audience: shareAudience,
            everyonePermission,
            ids: shareAudience === SHARE_AUDIENCE_SELECTED ? [ ...sharedWith ] : [],
            permissions: { ...sharePermissions }
        });
    };
    const close = () => {
        setDraft(null);
        toggleRef.current?.focus();
    };
    const onToggle = id => setDraft(current => {
        return { ...current,
            ids: current.ids.includes(id)
                ? current.ids.filter(other => other !== id)
                : [ ...current.ids, id ] };
    });
    const onPermission = (id, value) => setDraft(current => {
        return { ...current,
            permissions: { ...current.permissions,
                [id]: toPermission(value) } };
    });
    const apply = () => {
        if (draft.audience === SHARE_AUDIENCE_EVERYONE) {
            dispatch(shareNotes([], { audience: SHARE_AUDIENCE_EVERYONE,
                everyonePermission: draft.everyonePermission }));
        } else {
            dispatch(shareNotes(draft.ids.filter(id => remote.some(p => p.id === id)),
                { audience: SHARE_AUDIENCE_SELECTED,
                    permissions: draft.permissions }));
        }
        close();
    };
    const stopSharing = () => {
        dispatch(shareNotes([]));
        close();
    };
    const setAudience = audience => setDraft(current => {
        return { ...current,
            audience };
    });
    const status = shareAudience === SHARE_AUDIENCE_EVERYONE
        ? t('notesPane.sharedEveryone')
        : sharedWith.length ? t('notesPane.sharedWith', { count: sharedWith.length }) : t('notesPane.private');

    return (
        <div className = 'notes_pane-share'>
            <div className = 'notes_pane-share-head'>
                <div className = 'notes_pane-share-summary'>
                    <h2>{t('notesPane.myNotes')}</h2>
                    <span className = { `notes_pane-share-status${sharing ? ' is-shared' : ''}` }>
                        {sharing ? (
                            <Icon
                                size = { 14 }
                                src = { IconUserGroup } />
                        ) : <LockIcon />}
                        {status}
                    </span>
                </div>
                <button
                    aria-expanded = { isOpen }
                    aria-haspopup = 'dialog'
                    className = 'notes_pane-share-toggle'
                    disabled = { pickerDisabled }
                    onClick = { open }
                    ref = { toggleRef }
                    type = 'button'>
                    <Icon
                        size = { 16 }
                        src = { IconShare } />
                    <span>{sharing ? t('notesPane.manageAccess') : t('notesPane.share')}</span>
                </button>
            </div>
            {draft && (
                <div
                    aria-labelledby = 'notes-share-title'
                    aria-modal = 'true'
                    className = 'notes_pane-share-picker'
                    onKeyDown = { event => {
                        if (event.key === 'Escape') {
                            event.stopPropagation();
                            close();
                        }
                    } }
                    ref = { dialogRef }
                    role = 'dialog'
                    tabIndex = { -1 }>
                    <div className = 'notes_pane-share-title'>
                        <h2 id = 'notes-share-title'>
                            {sharing ? t('notesPane.manageAccess') : t('notesPane.shareTitle')}
                        </h2>
                        <button
                            aria-label = { t('notesPane.cancel') }
                            className = 'notes_pane-share-close'
                            onClick = { close }
                            type = 'button'>
                            <Icon
                                size = { 20 }
                                src = { IconClose } />
                        </button>
                    </div>
                    <fieldset className = 'notes_pane-share-audiences'>
                        <legend className = 'notes_pane-share-legend'>{t('notesPane.shareAudience')}</legend>
                        <label className = 'notes_pane-share-audience'>
                            <input
                                checked = { draft.audience === SHARE_AUDIENCE_EVERYONE }
                                name = 'notes-share-audience'
                                onChange = { () => setAudience(SHARE_AUDIENCE_EVERYONE) }
                                type = 'radio' />
                            <Icon
                                size = { 20 }
                                src = { IconGlobeBlack } />
                            <span className = 'notes_pane-share-audience-text'>
                                <span>{t('notesPane.shareEveryone')}</span>
                                <small>{t('notesPane.shareEveryoneHint')}</small>
                            </span>
                        </label>
                        <label className = 'notes_pane-share-audience'>
                            <input
                                checked = { draft.audience === SHARE_AUDIENCE_SELECTED }
                                name = 'notes-share-audience'
                                onChange = { () => setAudience(SHARE_AUDIENCE_SELECTED) }
                                type = 'radio' />
                            <Icon
                                size = { 20 }
                                src = { IconUserGroup } />
                            <span className = 'notes_pane-share-audience-text'>
                                <span>{t('notesPane.shareSelected')}</span>
                            </span>
                        </label>
                    </fieldset>
                    {draft.audience === SHARE_AUDIENCE_EVERYONE ? (
                        <div className = 'notes_pane-share-everyone'>
                            <label className = 'notes_pane-share-row'>
                                <span className = 'notes_pane-share-name'>{t('notesPane.everyoneAccess')}</span>
                                <select
                                    aria-label = { t('notesPane.everyoneAccess') }
                                    onChange = { event => setDraft(current => {
                                        return { ...current,
                                            everyonePermission: toPermission(event.target.value) };
                                    }) }
                                    value = { draft.everyonePermission }>
                                    <option value = { SHARE_PERMISSION_VIEW }>{t('notesPane.canView')}</option>
                                    <option value = { SHARE_PERMISSION_EDIT }>{t('notesPane.canEdit')}</option>
                                </select>
                            </label>
                        </div>
                    ) : remote.length ? (
                        <div className = 'notes_pane-share-people'>
                            <div className = 'notes_pane-share-search'>
                                <Icon
                                    size = { 16 }
                                    src = { IconSearch } />
                                <input
                                    aria-label = { t('notesPane.searchPeople') }
                                    onChange = { event => setQuery(event.target.value) }
                                    placeholder = { t('notesPane.searchPeople') }
                                    type = 'search'
                                    value = { query } />
                            </div>
                            <p
                                aria-live = 'polite'
                                className = 'notes_pane-share-hint'>
                                {t('notesPane.selectedCount', { count: draft.ids.length,
                                    total: remote.length })}
                            </p>
                            {rows.length ? (
                                <List
                                    className = 'notes_pane-share-list'
                                    defaultHeight = { ROW_HEIGHT * 8 }
                                    rowComponent = { NotesSharePerson }
                                    rowCount = { rows.length }
                                    rowHeight = { ROW_HEIGHT }
                                    rowProps = {{ draft,
                                        onPermission,
                                        onToggle,
                                        rows,
                                        t }} />
                            ) : <p className = 'notes_pane-share-hint'>{t('notesPane.noMatches')}</p>}
                        </div>
                    ) : <p className = 'notes_pane-share-hint'>{t('notesPane.noParticipants')}</p>}
                    <div className = 'notes_pane-share-buttons'>
                        {sharing && (
                            <button
                                className = 'notes_pane-danger'
                                onClick = { stopSharing }
                                type = 'button'>{t('notesPane.stopSharing')}</button>
                        )}
                        <button
                            className = 'notes_pane-secondary'
                            onClick = { close }
                            type = 'button'>{t('notesPane.cancel')}</button>
                        <button
                            className = 'notes_pane-primary'
                            onClick = { apply }
                            type = 'button'>{t('notesPane.shareApply')}</button>
                    </div>
                </div>
            )}
        </div>
    );
}
